import React from "react";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import { hasActiveProductsToBeDisplayed } from "@qgiv/p2p-registration";
import { selectAllCategories } from "../redux/slices/categoriesSlice";
import { selectAllP2pSettings } from "../redux/slices/p2pSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
    selectUnconfirmedRegistrations,
    setAllowEarlyFormSubmissionRegistrationPage,
    setAllowEarlyFormSubmissionStorePage,
} from "../redux/slices/registrationSlice";
import { selectAllProducts } from "../redux/slices/productsSlice";
import UpdateEarlySubmission from "./UpdateEarlySubmission";

const ConnectedUpdateEarlySubmission = () => {
    const {
        currentRegistrationCategory = [],
        currentRegistration,
        allowEarlyFormSubmissionRegistrationPage,
        allowEarlyFormSubmissionStorePage,
        promoDiscounts,
        registrations,
        storeCart,
    } = useSelector(selectAllRegistration);

    const { total = 0 } = useSelector(selectRegistrationTotals);

    const { remainingUnconfirmedRegistrations } = useSelector(
        selectUnconfirmedRegistrations,
    );

    const categories = useSelector(selectAllCategories);
    const { cms } = constants;

    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableOptionalDonation } = p2pSettings;

    const products = useSelector(selectAllProducts);

    const selectedCategoryId =
        currentRegistrationCategory.length > 0 &&
        Number(currentRegistrationCategory[0]);

    const selectedCategory =
        selectedCategoryId &&
        categories.map((category) => category.id).includes(selectedCategoryId)
            ? categories.find((category) => category.id === selectedCategoryId)
            : {};

    const { participants = [] } = promoDiscounts;

    let currentRegistrationTotal = 0;
    if (Object.keys(selectedCategory).length > 0) {
        const { earlyRegistration, fee, registrationFee } = selectedCategory;

        currentRegistrationTotal = registrationFee;

        if (earlyRegistration) {
            currentRegistrationTotal = fee;
        }

        if (
            participants.length > 0 &&
            participants[currentRegistration] &&
            participants[currentRegistration].CategoryId
        ) {
            currentRegistrationTotal =
                participants[currentRegistration].postPromoCategoryFee;
        }
        // when transaction promoCodes are implemented, will need to account for that here as well
    }

    const hasProducts = hasActiveProductsToBeDisplayed(products);

    // A registration that has a selected category with commitments enabled
    // should prevent early registration.
    // we need to add 3 checks:
    // 1. If current selected category has commitments
    // 2. If previously selected categories have commitments
    // 3. If commitments is enabled on the event level
    const eventEnableCommitments = !!p2pSettings.enableCommitments;
    const selectedCategoryEnableCommitments =
        !!selectedCategory?.enableCommitments;

    // the current registration index on the registrations array is going
    // to be an empty object. This is fine since we are looking for
    // any registrations where the enableCommitments flag is true
    const selectedCategoryPastRegistrations = registrations.map(
        (registration) => {
            const registrationKeys = Object.keys(registration);
            // prefilled registration data can include things like Team before
            // the registration is filled out via url shortcuts. Need to look
            // specifically for Category existing on the registration object
            if (registrationKeys.includes("Category")) {
                const currentSelectedCategory = categories.find(
                    (category) =>
                        category.id === Number(registration.Category[0]),
                ) || { enabledCommitments: false };
                const { enableCommitments = false } = currentSelectedCategory;
                return { enableCommitments };
            }
            return { enabledCommitments: false };
        },
    );
    const previousRegistrationsHaveEnableCommitments =
        selectedCategoryPastRegistrations.some(
            (registration) => registration.enableCommitments === true,
        );

    const registrationIncludesCategoryWithCommitments =
        selectedCategoryEnableCommitments ||
        previousRegistrationsHaveEnableCommitments;

    const allowEarlySubmitRespectingCommitments =
        !eventEnableCommitments || !registrationIncludesCategoryWithCommitments;

    const registrationPageSubmissionCheck = () =>
        remainingUnconfirmedRegistrations.length === 0 &&
        currentRegistrationTotal + total === 0 &&
        allowEarlySubmitRespectingCommitments &&
        !enableOptionalDonation &&
        !hasProducts;

    const storePageSubmissionCheck = () =>
        // Since there could be $0 dollar products in a store cart
        // and a shipping method which costs money
        // and those methods are only selectable on the payment page,
        // we need the store cart to be empty for early submission
        remainingUnconfirmedRegistrations.length === 0 &&
        storeCart.length === 0 &&
        allowEarlySubmitRespectingCommitments &&
        total === 0 &&
        !enableOptionalDonation;

    const updateEarlySubmissionProps = {
        allowEarlyFormSubmissionRegistrationPage,
        allowEarlyFormSubmissionStorePage,
        shouldAllowEarlyFormSubmissionRegistrationPage:
            registrationPageSubmissionCheck(),
        shouldAllowEarlyFormSubmissionStorePage: storePageSubmissionCheck(),
        isCms: cms === "1",
        setAllowEarlyFormSubmissionRegistrationPage,
        setAllowEarlyFormSubmissionStorePage,
    };

    return <UpdateEarlySubmission {...updateEarlySubmissionProps} />;
};

export default ConnectedUpdateEarlySubmission;
